
import { useCallback } from 'react';
import { useUpdateProject } from './useProjects';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { supabase } from '@/services/supabase/supabase';

export const useProjectSettings = (projectId: string) => {
    // Optimized query to fetch only settings
    const { data: project } = useQuery({
        queryKey: queryKeys.projects.settings(projectId),
        queryFn: async () => {
            if (!projectId) return null;
            const { data, error } = await supabase
                .from('projects')
                .select('settings')
                .eq('id', projectId)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !!projectId,
        staleTime: 1000 * 60 * 5 // 5 minutes
    });

    const { mutateAsync: updateProject } = useUpdateProject();

    const settings = project?.settings || {};

    const updateSettings = useCallback(async (newSettings: Record<string, any>) => {
        if (!project) return;

        const updatedSettings = {
            ...settings,
            ...newSettings
        };

        await updateProject({
            id: projectId,
            settings: updatedSettings
        });
    }, [project, settings, updateProject, projectId]);

    const useIntroDismissal = (pageKey: string) => {
        const isDismissed = settings?.intro_dismissed?.[pageKey] === true;

        const dismiss = useCallback(async () => {
            const currentIntroDismissed = settings?.intro_dismissed || {};
            await updateSettings({
                intro_dismissed: {
                    ...currentIntroDismissed,
                    [pageKey]: true
                }
            });
        }, [settings, updateSettings, pageKey]);

        return { isDismissed, dismiss };
    };

    return {
        settings,
        updateSettings,
        useIntroDismissal
    };
};
